import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../store";
import { login, clearError } from "../../features/auth/authSlice";
import "./login.css";

// Basit validation şeması
const schema = yup
  .object({
    username: yup.string().required("Username is required").trim(),
    password: yup.string().required("Password is required"),
  })
  .required();

// Form için tip tanımlaması
type LoginFormInputs = {
  username: string;
  password: string;
};

const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, error } = useAppSelector(
    (state) => state.auth
  );

  // State tanımlamaları
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // Ref tanımlamaları
  const cardRef = useRef<HTMLDivElement>(null);

  // React Hook Form - basit konfigürasyon
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<LoginFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  // Form değerlerini izle
  const watchedValues = watch();

  // Kullanıcı zaten giriş yapmışsa dashboard'a yönlendir
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Component unmount olduğunda temizlik
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Mouse hareketini takip et
  const handleMouseMove = useCallback((e: MouseEvent) => {
    setMousePosition({
      x: (e.clientX / window.innerWidth) * 100,
      y: (e.clientY / window.innerHeight) * 100,
    });
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [handleMouseMove]);

  // Form gönderildiğinde
  const onSubmit = useCallback(
    (data: LoginFormInputs) => {
      // Remember me durumunu localStorage'a kaydet
      if (rememberMe) {
        localStorage.setItem("rememberMe", "true");
        localStorage.setItem("lastUsername", data.username);
      } else {
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("lastUsername");
      }

      dispatch(login(data));
    },
    [dispatch, rememberMe]
  );

  // Şifreyi göster/gizle
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  // Remember me toggle
  const handleRememberMeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRememberMe(e.target.checked);
    },
    []
  );

  // Input focus handlers
  const handleInputFocus = useCallback((fieldName: string) => {
    setFocusedField(fieldName);
  }, []);

  const handleInputBlur = useCallback(() => {
    setFocusedField(null);
  }, []);

  // Placeholder'ın görünüp görünmeyeceğini kontrol et
  const shouldShowPlaceholder = useCallback(
    (fieldName: keyof LoginFormInputs, placeholder: string) => {
      const fieldValue = watchedValues[fieldName];
      const isFocused = focusedField === fieldName;

      // Eğer alan odaklanmışsa veya değer varsa placeholder'ı gizle
      if (isFocused || (fieldValue && fieldValue.length > 0)) {
        return "";
      }
      return placeholder;
    },
    [watchedValues, focusedField]
  );

  // Register fonksiyonlarını özelleştir
  const usernameRegister = register("username");
  const passwordRegister = register("password");

  // Custom event handlers
  const handleUsernameFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      handleInputFocus("username");
      usernameRegister.onBlur(e); // React Hook Form'un kendi handler'ını çağır
    },
    [handleInputFocus, usernameRegister]
  );

  const handleUsernameBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      handleInputBlur();
      usernameRegister.onBlur(e); // React Hook Form'un kendi handler'ını çağır
    },
    [handleInputBlur, usernameRegister]
  );

  const handlePasswordFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      handleInputFocus("password");
      passwordRegister.onBlur(e); // React Hook Form'un kendi handler'ını çağır
    },
    [handleInputFocus, passwordRegister]
  );

  const handlePasswordBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      handleInputBlur();
      passwordRegister.onBlur(e); // React Hook Form'un kendi handler'ını çağır
    },
    [handleInputBlur, passwordRegister]
  );

  // Partikülleri memoize et
  const particles = useMemo(() => {
    const particleArray = [];
    for (let i = 0; i < 20; i++) {
      particleArray.push({
        key: i,
        style: {
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 20}s`,
          animationDuration: `${20 + Math.random() * 10}s`,
        },
      });
    }
    return particleArray;
  }, []);

  // Remember me durumunu localStorage'dan oku
  useEffect(() => {
    const savedRememberMe = localStorage.getItem("rememberMe") === "true";
    const savedUsername = localStorage.getItem("lastUsername");

    if (savedRememberMe && savedUsername) {
      setRememberMe(true);
      reset({ username: savedUsername, password: "" });
    }
  }, [reset]);

  return (
    <div className="auth-page">
      {/* Animasyonlu arka plan */}
      <div className="background-animation">
        <div
          className="gradient-sphere"
          style={{
            left: `${mousePosition.x}%`,
            top: `${mousePosition.y}%`,
          }}
        />
        <div className="grid-overlay" />
        <div className="particles">
          {particles.map((particle) => (
            <div
              key={particle.key}
              className="particle"
              style={particle.style}
            />
          ))}
        </div>
      </div>

      <div className="auth-container">
        <div className="auth-card-wrapper">
          <div ref={cardRef} className="auth-card">
            {/* Logo */}
            <div className="auth-logo">
              <div className="logo-circle">
                <div className="logo-inner" />
              </div>
              <h1>AI Digital Marketing</h1>
              <p>Sign in to your account</p>
            </div>

            {/* Hata mesajı */}
            {error && (
              <div className="auth-error" role="alert">
                {error}
              </div>
            )}

            {/* Login formu */}
            <form className="auth-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="auth-input-group">
                <label className="auth-label" htmlFor="username">
                  Username
                </label>
                <div className="input-container">
                  <input
                    type="text"
                    className={`auth-input ${errors.username ? "is-invalid" : ""}`}
                    id="username"
                    placeholder={shouldShowPlaceholder(
                      "username",
                      "Enter your username"
                    )}
                    autoComplete="username"
                    {...usernameRegister}
                    onFocus={handleUsernameFocus}
                    onBlur={handleUsernameBlur}
                  />
                </div>
                {errors.username && (
                  <div className="error-message">{errors.username.message}</div>
                )}
              </div>

              <div className="auth-input-group">
                <label className="auth-label" htmlFor="password">
                  Password
                </label>
                <div className="input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    className={`auth-input ${errors.password ? "is-invalid" : ""}`}
                    id="password"
                    placeholder={shouldShowPlaceholder(
                      "password",
                      "Enter your password"
                    )}
                    autoComplete="current-password"
                    {...passwordRegister}
                    onFocus={handlePasswordFocus}
                    onBlur={handlePasswordBlur}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={togglePasswordVisibility}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <div className="error-message">{errors.password.message}</div>
                )}
              </div>

              <div className="auth-options">
                <div className="remember-me">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    className="custom-checkbox"
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                  />
                  <label htmlFor="rememberMe">Remember me</label>
                </div>
                <Link to="/forgot-password" className="forgot-link">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                className={`auth-button ${isLoading ? "loading" : ""}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner"></span>
                    <span>Signing in...</span>
                  </>
                ) : (
                  "Sign In"
                )}
              </button>

              <div className="register-prompt">
                <p>
                  Don't have an account?{" "}
                  <Link to="/register" className="register-link">
                    Sign up
                  </Link>
                </p>
              </div>
            </form>
          </div>

          <div className="copyright">
            <p>
              &copy; {new Date().getFullYear()} AI Digital Marketing Platform
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
